import { useState } from 'react';

import { useMutation, useQuery } from '@tanstack/react-query';

import { COOKIE_EXPIRATION_PERIOD_MINUTES } from '../constants';
import { type ExtendedAirgapAPI, type TranscendConsent } from '../types';

import { RQ_CONSENT_RECONCILIATION } from '@/modules/ReactQuery/cacheKeys';
import { getPersistentId } from '@/modules/activityTracker/helpers';
import {
  type ReconcileConsentRequest,
  type ReconcileConsentResponse,
} from '@/modules/consent/aliases';
import { getConsentReconciliation } from '@/modules/consent/api';
import { useConsentManager } from '@/modules/consent/hooks/useConsentManager';
import { useSessionId } from '@/modules/storage/useSessionId';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type AirGapConsent = {
  Advertising?: boolean;
  Functional?: boolean;
  Analytics?: boolean;
  Saleofinfo?: boolean;
};

type ConsentItem = {
  value: boolean | 'Auto';
  isConfirmed: boolean;
  expirationDate: number;
};

async function assembleTranscendPayload({
  airgap,
  sessionId,
  userId,
}: {
  airgap: ExtendedAirgapAPI;
  sessionId?: string;
  userId?: number;
}) {
  const storedConsent = airgap?.getConsent() as TranscendConsent;
  const persistentId = await getPersistentId();
  const payload: ReconcileConsentRequest = {
    persistentId,
    userId,
    platform: 'web',
    isConfirmed: true,
    consentPayload: {
      purposes: storedConsent?.purposes,
      confirmed: storedConsent?.confirmed,
      timestamp: storedConsent?.timestamp,
      prompted: storedConsent?.prompted || false,
      updated: storedConsent?.updated || false,
    },
    expirationPeriodMinutes:
      Number(airgap.loadOptions.consentExpiry) ||
      COOKIE_EXPIRATION_PERIOD_MINUTES,
    applicablePurposes: Array.from(airgap?.getRegimePurposes() || []),
    regionalExperiences: Array.from(airgap?.getRegimes() || []),
    sessionId,
  };
  return payload;
}

function getConsentConfirmation(data: ReconcileConsentResponse) {
  return Object.values(data)
    .filter(
      (item): item is ConsentItem =>
        !Array.isArray(item) &&
        typeof item === 'object' &&
        item !== null &&
        'isConfirmed' in item &&
        typeof item.value === 'boolean'
    )
    .every((item) => item.isConfirmed === true);
}

async function updateAirgapConsent(
  airgap: ExtendedAirgapAPI,
  data: ReconcileConsentResponse,
  airgapLoadedEvent?: Event
) {
  const isConsentConfirmed = getConsentConfirmation(data);
  const consentPayload = (
    Object.keys(data) as Array<keyof AirGapConsent>
  ).reduce<AirGapConsent>((acc, key) => {
    const excludedKeys = ['regionalExperiences', 'updatedAt'];
    const item = data[key as keyof ReconcileConsentResponse];
    if (
      excludedKeys.includes(key) ||
      Array.isArray(item) ||
      typeof item === 'string' ||
      item?.value === 'Auto'
    ) {
      return acc;
    }
    acc[(key.charAt(0).toUpperCase() + key.slice(1)) as keyof AirGapConsent] =
      item?.value === true;
    return acc;
  }, {});
  // the method returns a boolean to indicate if the consent was set successfully.
  // this will be false if there is some issue with the object passed to it, like
  // a missing or invalid event.
  if (airgapLoadedEvent) {
    // we set confirmed and prompted ourselves so we use what the backend tells us
    // to work out when to show or hide the banner
    const isConsentSet = await airgap.setConsent(
      airgapLoadedEvent,
      consentPayload,
      {
        confirmed: isConsentConfirmed,
        prompted: isConsentConfirmed,
        timestamp: data.updatedAt,
      }
    );
    if (!isConsentSet) {
      console.error('Failed to set consent');
    }
  }
}

/**
 * The second optional param, preventReconciliationCall will only be true
 * when we call this from the footer in a hidden state. This is to prevent
 * the reconciliation call from being made when we don't want it to be.
 * ATM that is mainly during the login flow when we have reset the logged out
 * consent for the user and don't want to overwrite it until they've completed
 * the login flow.
 *
 * @param preventReconciliationCall only executes api calls if this is true
 */
export function useConsentReconciliation(preventReconciliationCall = false) {
  const [reconciledConsentData, setReconciledConsentData] = useState<
    ReconcileConsentResponse | undefined
  >(undefined);

  const { airgap, airgapLoadedEvent, transcend } = useConsentManager();
  const sessionId = useSessionId() || undefined;
  const [, user] = useCurrentUser();

  useQuery({
    enabled: airgap && !preventReconciliationCall && !!sessionId,
    queryKey: [RQ_CONSENT_RECONCILIATION, airgap],
    queryFn: async () => {
      if (airgap) {
        const payload = await assembleTranscendPayload({
          airgap,
          sessionId,
          userId: user?.id,
        });
        const { data } = await getConsentReconciliation(payload);
        if (airgapLoadedEvent) {
          updateAirgapConsent(airgap, data, airgapLoadedEvent);
        }
        setReconciledConsentData(data);
        return data;
      }
      return {};
    },
  });

  const reconcileConsentMutation = useMutation({
    mutationFn: async (changeConfirmed: boolean = false) => {
      if (!airgap || !sessionId) {
        return {};
      }
      const payload = await assembleTranscendPayload({
        airgap,
        sessionId,
        userId: user?.id,
      });
      const payloadToSend = changeConfirmed
        ? { ...payload, isConfirmed: false }
        : payload;
      const { data } = await getConsentReconciliation(payloadToSend);
      updateAirgapConsent(airgap, data, airgapLoadedEvent);
      reconcileConsentMutation.reset();
    },
  });

  return {
    reconciledConsentData,
    transcend,
    airgap,
    storedConsent: airgap?.getConsent(),
    reconcileConsentMutation,
  };
}
